// src/components/reporting/usage/UsageReport.js
// Top-level container for the Usage & Cost report.
// Manages: month/year picker, two-level drill-down (system → tenant), CSV export.
// SuperAdmin only — enforced at the API level; this component is only rendered
// when isSuperAdmin is true in ReportingDashboard.

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useUsageData from '../../../hooks/useUsageData';
import UsageSummaryPanel, { centsToDollars } from './UsageSummaryPanel';
import UsageTenantList from './UsageTenantList';
import UsageUserList from './UsageUserList';
import { exportToCSV } from '../../../utils/csvExport';
import '../../../styles/globals.css';
import '../Reporting.css';
import './UsageReport.css';

// ─── Date helpers ─────────────────────────────────────────────────────────────

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Returns { startDate, endDate } strings (YYYY-MM-DD) for the given month/year */
const periodFromMonthYear = (year, month) => {
  // month is 0-indexed
  const start = new Date(year, month, 1);
  const end   = new Date(year, month + 1, 0); // last day of month
  const fmt   = (d) => d.toISOString().split('T')[0];
  return { startDate: fmt(start), endDate: fmt(end) };
};

/** Builds an array of { year, month } options — current month back 24 months */
const buildMonthOptions = () => {
  const options = [];
  const now = new Date();
  const earliestDate = new Date(2026, 0, 1); // January 2026
  
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    
    // Stop if we've gone before January 2026
    if (d < earliestDate) break;
    
    options.push({ year: d.getFullYear(), month: d.getMonth() });
  }
  return options;
};

// ─── CSV helpers ──────────────────────────────────────────────────────────────

/** Flatten a services + totals object into one row for CSV */
const flattenSummaryForCSV = (summary) => {
  if (!summary?.services) return {};
  const { services, totals } = summary;
  return {
    conversation_calls:        services.conversation?.callCount       ?? 0,
    conversation_inputTokens:  services.conversation?.inputTokens     ?? 0,
    conversation_outputTokens: services.conversation?.outputTokens    ?? 0,
    conversation_cost:         centsToDollars(services.conversation?.totalCostCents),
    analytic_calls:            services.analytic_report?.callCount    ?? 0,
    analytic_inputTokens:      services.analytic_report?.inputTokens  ?? 0,
    analytic_outputTokens:     services.analytic_report?.outputTokens ?? 0,
    analytic_cost:             centsToDollars(services.analytic_report?.totalCostCents),
    adhoc_calls:               services.ad_hoc_report?.callCount      ?? 0,
    adhoc_inputTokens:         services.ad_hoc_report?.inputTokens    ?? 0,
    adhoc_outputTokens:        services.ad_hoc_report?.outputTokens   ?? 0,
    adhoc_cost:                centsToDollars(services.ad_hoc_report?.totalCostCents),
    assessment_calls:          services.assessment_report?.callCount   ?? 0,
    assessment_inputTokens:    services.assessment_report?.inputTokens ?? 0,
    assessment_outputTokens:   services.assessment_report?.outputTokens ?? 0,
    assessment_cost:           centsToDollars(services.assessment_report?.totalCostCents),
    stt_calls:                 services.stt?.callCount                ?? 0,
    stt_durationSeconds:       services.stt?.durationSeconds          ?? 0,
    stt_cost:                  centsToDollars(services.stt?.totalCostCents),
    tts_calls:                 services.tts?.callCount                ?? 0,
    tts_characters:            services.tts?.characterCount           ?? 0,
    tts_cost:                  centsToDollars(services.tts?.totalCostCents),
    total_calls:               totals?.totalCallCount                 ?? 0,
    total_cost:                centsToDollars(totals?.totalCostCents),
  };
};

const SYSTEM_CSV_COLUMNS = [
  { key: 'tenantName',              label: 'Tenant' },
  { key: 'tenantSlug',              label: 'Slug' },
  { key: 'conversation_calls',      label: 'Conversation Calls' },
  { key: 'conversation_inputTokens',label: 'Conv. Input Tokens' },
  { key: 'conversation_outputTokens',label:'Conv. Output Tokens' },
  { key: 'conversation_cost',       label: 'Conversation Cost' },
  { key: 'analytic_calls',          label: 'Analytic Report Calls' },
  { key: 'analytic_inputTokens',    label: 'Analytic Input Tokens' },
  { key: 'analytic_outputTokens',   label: 'Analytic Output Tokens' },
  { key: 'analytic_cost',           label: 'Analytic Report Cost' },
  { key: 'adhoc_calls',             label: 'Ad Hoc Calls' },
  { key: 'adhoc_inputTokens',       label: 'Ad Hoc Input Tokens' },
  { key: 'adhoc_outputTokens',      label: 'Ad Hoc Output Tokens' },
  { key: 'adhoc_cost',              label: 'Ad Hoc Cost' },
  { key: 'assessment_calls',        label: 'Assessment Calls' },
  { key: 'assessment_inputTokens',  label: 'Assessment Input Tokens' },
  { key: 'assessment_outputTokens', label: 'Assessment Output Tokens' },
  { key: 'assessment_cost',         label: 'Assessment Cost' },
  { key: 'stt_calls',               label: 'STT Calls' },
  { key: 'stt_durationSeconds',     label: 'STT Duration (s)' },
  { key: 'stt_cost',                label: 'STT Cost' },
  { key: 'tts_calls',               label: 'TTS Calls' },
  { key: 'tts_characters',          label: 'TTS Characters' },
  { key: 'tts_cost',                label: 'TTS Cost' },
  { key: 'total_calls',             label: 'Total Calls' },
  { key: 'total_cost',              label: 'Total Cost' },
];

const TENANT_CSV_COLUMNS = [
  { key: 'displayName',             label: 'User' },
  { key: 'email',                   label: 'Email' },
  { key: 'conversation_calls',      label: 'Conversation Calls' },
  { key: 'conversation_inputTokens',label: 'Conv. Input Tokens' },
  { key: 'conversation_outputTokens',label:'Conv. Output Tokens' },
  { key: 'conversation_cost',       label: 'Conversation Cost' },
  { key: 'analytic_calls',          label: 'Analytic Report Calls' },
  { key: 'analytic_inputTokens',    label: 'Analytic Input Tokens' },
  { key: 'analytic_outputTokens',   label: 'Analytic Output Tokens' },
  { key: 'analytic_cost',           label: 'Analytic Report Cost' },
  { key: 'adhoc_calls',             label: 'Ad Hoc Calls' },
  { key: 'adhoc_inputTokens',       label: 'Ad Hoc Input Tokens' },
  { key: 'adhoc_outputTokens',      label: 'Ad Hoc Output Tokens' },
  { key: 'adhoc_cost',              label: 'Ad Hoc Cost' },
  { key: 'assessment_calls',        label: 'Assessment Calls' },
  { key: 'assessment_inputTokens',  label: 'Assessment Input Tokens' },
  { key: 'assessment_outputTokens', label: 'Assessment Output Tokens' },
  { key: 'assessment_cost',         label: 'Assessment Cost' },
  { key: 'stt_calls',               label: 'STT Calls' },
  { key: 'stt_durationSeconds',     label: 'STT Duration (s)' },
  { key: 'stt_cost',                label: 'STT Cost' },
  { key: 'tts_calls',               label: 'TTS Calls' },
  { key: 'tts_characters',          label: 'TTS Characters' },
  { key: 'tts_cost',                label: 'TTS Cost' },
  { key: 'total_calls',             label: 'Total Calls' },
  { key: 'total_cost',              label: 'Total Cost' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Breadcrumb bar */
const Breadcrumbs = ({ crumbs }) => (
  <nav className="breadcrumb-nav text-sm text-gray-600 mb-4" aria-label="Breadcrumb">
    {crumbs.map((crumb, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="mx-2 text-gray-400">/</span>}
        {crumb.onClick ? (
          <button
            onClick={crumb.onClick}
            className="text-blue-600 hover:underline"
          >
            {crumb.label}
          </button>
        ) : (
          <span className="text-gray-800 font-medium">{crumb.label}</span>
        )}
      </React.Fragment>
    ))}
  </nav>
);

Breadcrumbs.propTypes = {
  crumbs: PropTypes.arrayOf(PropTypes.shape({
    label:   PropTypes.string.isRequired,
    onClick: PropTypes.func,
  })).isRequired,
};

/** Month/year picker */
const MonthPicker = ({ selectedYear, selectedMonth, onChange }) => {
  const options = buildMonthOptions();

  const handleChange = (e) => {
    const [year, month] = e.target.value.split('-').map(Number);
    onChange(year, month);
  };

  return (
    <div className="usage-month-picker">
      <label className="usage-month-label" htmlFor="usage-month-select">
        Reporting Period
      </label>
      <select
        id="usage-month-select"
        className="usage-month-select"
        value={`${selectedYear}-${selectedMonth}`}
        onChange={handleChange}
      >
        {options.map(({ year, month }) => (
          <option key={`${year}-${month}`} value={`${year}-${month}`}>
            {MONTHS[month]} {year}
          </option>
        ))}
      </select>
    </div>
  );
};

MonthPicker.propTypes = {
  selectedYear:  PropTypes.number.isRequired,
  selectedMonth: PropTypes.number.isRequired,
  onChange:      PropTypes.func.isRequired,
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UsageReport = ({ onBack }) => {
  const { loading, error, clearError, fetchSystemwide, fetchTenantUsage } = useUsageData();

  // Date state — default to current month
  const now = new Date();
  const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());

  // Level: 'system' | 'tenant'
  const [level, setLevel] = useState('system');

  // Data
  const [systemData, setSystemData]   = useState(null);
  const [tenantData, setTenantData]   = useState(null);
  const [activeTenant, setActiveTenant] = useState(null); // the tenantSummary row clicked

  // ── Load system data ──────────────────────────────────────────────────────
  const loadSystemwide = useCallback(async (year, month) => {
    clearError();
    const period = periodFromMonthYear(year, month);
    try {
      const data = await fetchSystemwide(period);
      setSystemData(data);
    } catch {
      // error state handled by hook
    }
  }, [fetchSystemwide, clearError]);

  useEffect(() => {
    loadSystemwide(selectedYear, selectedMonth);
  }, [loadSystemwide, selectedYear, selectedMonth]);

  // ── Load tenant data ──────────────────────────────────────────────────────
  const loadTenant = useCallback(async (tenant, year, month) => {
    clearError();
    const period = periodFromMonthYear(year, month);
    try {
      const data = await fetchTenantUsage(tenant.tenantId, period);
      setTenantData(data);
      setActiveTenant(tenant);
      setLevel('tenant');
    } catch {
      // error state handled by hook
    }
  }, [fetchTenantUsage, clearError]);

  // ── Month picker handler ──────────────────────────────────────────────────
  const handleMonthChange = (year, month) => {
    setSelectedYear(year);
    setSelectedMonth(month);
    // If we're viewing a tenant, reload tenant data for the new period
    if (level === 'tenant' && activeTenant) {
      loadTenant(activeTenant, year, month);
    }
    // System data reloads via the useEffect above
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const handleBackToSystem = () => {
    setLevel('system');
    setTenantData(null);
    setActiveTenant(null);
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const monthLabel = `${MONTHS[selectedMonth]}-${selectedYear}`;

    if (level === 'system') {
      if (!systemData?.tenantSummaries?.length) {
        alert('No tenant data available to export.');
        return;
      }
      const rows = systemData.tenantSummaries.map((t) => ({
        tenantName: t.tenantName,
        tenantSlug: t.tenantSlug,
        ...flattenSummaryForCSV({ services: t.services, totals: t.totals }),
      }));
      exportToCSV(rows, SYSTEM_CSV_COLUMNS, `usage-systemwide-${monthLabel}`);
    } else if (level === 'tenant') {
      if (!tenantData?.userSummaries?.length) {
        alert('No user data available to export.');
        return;
      }
      const rows = tenantData.userSummaries.map((u) => ({
        displayName: u.displayName || u.email,
        email:       u.email,
        ...flattenSummaryForCSV({ services: u.services, totals: u.totals }),
      }));
      const slug = activeTenant?.tenantSlug || 'tenant';
      exportToCSV(rows, TENANT_CSV_COLUMNS, `usage-${slug}-${monthLabel}`);
    }
  };

  // ── Breadcrumbs ───────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Reporting Dashboard', onClick: onBack },
    { label: 'Usage & Costs',       onClick: level === 'tenant' ? handleBackToSystem : undefined },
    ...(level === 'tenant' && activeTenant
      ? [{ label: activeTenant.tenantName }]
      : []),
  ];

  // ── Derived ───────────────────────────────────────────────────────────────
  const periodLabel = systemData?.period?.label
    || tenantData?.period?.label
    || `${MONTHS[selectedMonth]} ${selectedYear}`;

  const pageTitle = level === 'tenant' && activeTenant
    ? `${activeTenant.tenantName} — Usage & Costs`
    : 'Usage & Costs';

  const pageSubtitle = level === 'tenant' && activeTenant
    ? `Breakdown by user for ${periodLabel}`
    : `System-wide breakdown for ${periodLabel}`;

  const currentSummary = level === 'tenant'
    ? tenantData?.tenantSummary
    : systemData?.systemSummary;

  const exportDisabled = loading || (
    level === 'system'
      ? !systemData?.tenantSummaries?.length
      : !tenantData?.userSummaries?.length
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="usage-report">

      {/* Breadcrumbs */}
      <Breadcrumbs crumbs={breadcrumbs} />

      {/* Page header */}
      <div className="report-header">
        <div className="usage-report-header-row">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">{pageTitle}</h2>
            <p className="text-gray-600 mt-1">{pageSubtitle}</p>
          </div>

          <div className="usage-header-controls">
            {/* Month picker */}
            <MonthPicker
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              onChange={handleMonthChange}
            />

            {/* CSV export */}
            <button
              className="btn btn-secondary"
              onClick={handleExportCSV}
              disabled={exportDisabled}
              title="Download CSV"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>

            {/* Refresh */}
            <button
              className="btn btn-secondary"
              onClick={() =>
                level === 'tenant' && activeTenant
                  ? loadTenant(activeTenant, selectedYear, selectedMonth)
                  : loadSystemwide(selectedYear, selectedMonth)
              }
              disabled={loading}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="alert alert-error mb-6">
          <strong>Error:</strong> {error}
          <button
            onClick={() => {
              clearError();
              level === 'tenant' && activeTenant
                ? loadTenant(activeTenant, selectedYear, selectedMonth)
                : loadSystemwide(selectedYear, selectedMonth);
            }}
            className="btn btn-secondary btn-sm ml-3"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-12">
          <div className="loading-dots mx-auto mb-3">
            <div className="loading-dot" />
            <div className="loading-dot" />
            <div className="loading-dot" />
          </div>
          <p className="text-gray-600">Loading usage data…</p>
        </div>
      )}

      {/* Content — hidden while loading to prevent flash */}
      {!loading && !error && (
        <>
          {/* ── Summary panel ── */}
          <section className="usage-section">
            <h3 className="usage-section-title">
              {level === 'tenant' ? 'Tenant Summary' : 'System Summary'}
            </h3>
            <UsageSummaryPanel
              summary={currentSummary}
              periodLabel={null} /* shown in page subtitle */
            />
          </section>

          {/* ── Drill-down list ── */}
          <section className="usage-section">
            {level === 'system' ? (
              <>
                <h3 className="usage-section-title">
                  By Tenant
                  {systemData?.tenantSummaries?.length > 0 && (
                    <span className="usage-section-count">
                      {systemData.tenantSummaries.length} active
                    </span>
                  )}
                </h3>
                <p className="usage-section-hint">
                  Click a tenant row to see its user breakdown.
                </p>
                <UsageTenantList
                  tenantSummaries={systemData?.tenantSummaries}
                  onSelectTenant={(t) => loadTenant(t, selectedYear, selectedMonth)}
                />
              </>
            ) : (
              <>
                <h3 className="usage-section-title">
                  By User
                  {tenantData?.userSummaries?.length > 0 && (
                    <span className="usage-section-count">
                      {tenantData.userSummaries.length} active
                    </span>
                  )}
                </h3>
                <UsageUserList
                  userSummaries={tenantData?.userSummaries}
                />
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
};

UsageReport.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default UsageReport;